/**
 * hz-variant-count.js
 *
 * Renders the "N sizes available" / "N colors available" / "N fits available"
 * label inside every variant option fieldset of a product card.
 *
 * Loaded once from layout/theme.liquid. This replaces the inline script that
 * used to be emitted per product card in snippets/product-card.liquid, where
 * every card registered its own DOMContentLoaded handler plus a document-level
 * change listener that re-queried the whole document (O(N^2) on collections).
 *
 * Behaviour kept identical to the inline version:
 *   - operates on .variant-option fieldsets
 *   - counts input[type="radio"] elements without an aria-disabled attribute
 *   - replaces any existing .variant-count child and appends a fresh one
 *   - label text comes from the locales (hv.variants.sizes / colours / fits, one and other), passed in as
 *     data attributes on the script tag in layout/theme.liquid (leverene, 30 okt 2026: in de huisstijl en
 *     vertaalbaar, "7 sizes" in plaats van "7 sizes available"). Without them: English fallback.
 *   - option is detected from the first radio's name attribute
 *     (maat/size -> sizes, kleur/color -> colors, pasvorm/fit -> fits)
 *
 * What changed: one delegated change listener for the whole page, updates are
 * scoped to the product card that owns the changed radio, plus a debounced
 * MutationObserver on the product grid and a shopify:section:load hook so
 * re-rendered cards (filtering, pagination, section reload) get their labels
 * back exactly like the old per-card script did on load.
 */
(function () {
  'use strict';

  if (window.hzVariantCountLoaded) return;
  window.hzVariantCountLoaded = true;

  var SCRIPT = document.currentScript;
  var FALLBACK = {
    'sizes-one': '%N% size', 'sizes-other': '%N% sizes',
    'colours-one': '%N% colour', 'colours-other': '%N% colours',
    'fits-one': '%N% fit', 'fits-other': '%N% fits'
  };

  function labelFor(kind, count) {
    var key = kind + '-' + (count === 1 ? 'one' : 'other');
    var tpl = (SCRIPT && SCRIPT.getAttribute('data-' + key)) || FALLBACK[key] || '%N%';
    return tpl.replace('%N%', String(count));
  }

  var CARD_SELECTOR = 'product-card, .product-card';
  var OPTION_SELECTOR = '.variant-option';
  var RADIO_SELECTOR = 'input[type="radio"]';
  var COUNT_CLASS = 'variant-count';
  var GRID_SELECTOR = '.product-grid';
  var CHANGE_DELAY = 50;
  var MUTATION_DELAY = 100;

  var scopeTimers = new WeakMap();
  var observers = [];
  var observerTimer = null;

  /* ---------------------------------------------------------------- label */

  function countOptions(fieldset, kind) {
    var inputs = fieldset.querySelectorAll(RADIO_SELECTOR);
    var count = 0;

    for (var i = 0; i < inputs.length; i++) {
      if (!inputs[i].hasAttribute('aria-disabled')) {
        count++;
      }
    }

    var existing = fieldset.querySelector('.' + COUNT_CLASS);
    if (existing) existing.remove();

    var display = document.createElement('div');
    display.className = COUNT_CLASS;
    display.textContent = labelFor(kind, count);

    fieldset.appendChild(display);
  }

  function updateFieldset(fieldset) {
    var input = fieldset.querySelector(RADIO_SELECTOR);
    if (!input) return;

    var nameAttribute = input.getAttribute('name');
    if (!nameAttribute) return;

    var name = nameAttribute.toLowerCase();

    if (name.indexOf('maat') !== -1 || name.indexOf('size') !== -1) {
      countOptions(fieldset, 'sizes');
    } else if (name.indexOf('kleur') !== -1 || name.indexOf('color') !== -1 || name.indexOf('colour') !== -1) {
      countOptions(fieldset, 'colours');
    } else if (name.indexOf('pasvorm') !== -1 || name.indexOf('fit') !== -1) {
      countOptions(fieldset, 'fits');
    }
  }

  /* Update every variant option fieldset inside a single card or dialog. */
  function updateScope(scope) {
    if (!scope || !scope.querySelectorAll) return;

    var fieldsets = scope.querySelectorAll(OPTION_SELECTOR);
    for (var i = 0; i < fieldsets.length; i++) {
      updateFieldset(fieldsets[i]);
    }
  }

  /* Update every product card inside a root (document, or a reloaded section). */
  function updateAll(root) {
    var context = root && root.querySelectorAll ? root : document;

    if (context.matches && context.matches(CARD_SELECTOR)) {
      updateScope(context);
    }

    var cards = context.querySelectorAll(CARD_SELECTOR);
    for (var i = 0; i < cards.length; i++) {
      updateScope(cards[i]);
    }
  }

  /* --------------------------------------------------------------- events */

  function scheduleScope(scope) {
    var pending = scopeTimers.get(scope);
    if (pending) clearTimeout(pending);

    scopeTimers.set(
      scope,
      setTimeout(function () {
        scopeTimers.delete(scope);
        updateScope(scope);
      }, CHANGE_DELAY)
    );
  }

  /* One delegated listener for the whole page. */
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.matches || !target.matches(RADIO_SELECTOR)) return;

    /* Scope to the card that owns the radio. Quick add markup that is moved
       out of the card still resolves through its dialog ancestor. */
    var scope = target.closest(CARD_SELECTOR) || target.closest('dialog');
    if (!scope) return;

    scheduleScope(scope);
  });

  document.addEventListener('shopify:section:load', function (event) {
    updateAll(event.target || document);
    observeGrids();
  });

  /* ------------------------------------------------------------- observer */

  /* Records caused by our own label writes must not retrigger a run. */
  function onlyCountNodes(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.nodeType !== 1) return false;
      if (!node.classList || !node.classList.contains(COUNT_CLASS)) return false;
    }
    return true;
  }

  function handleMutations(records) {
    for (var i = 0; i < records.length; i++) {
      var record = records[i];
      if (onlyCountNodes(record.addedNodes) && onlyCountNodes(record.removedNodes)) continue;

      if (observerTimer) clearTimeout(observerTimer);
      observerTimer = setTimeout(function () {
        observerTimer = null;
        updateAll(document);
      }, MUTATION_DELAY);
      return;
    }
  }

  function gridRoots() {
    var roots = [];
    var grids = document.querySelectorAll(GRID_SELECTOR);
    var i;

    for (i = 0; i < grids.length; i++) {
      roots.push(grids[i]);
    }
    if (roots.length) return roots;

    /* Fall back to the list container that holds the cards. */
    var cards = document.querySelectorAll(CARD_SELECTOR);
    for (i = 0; i < cards.length; i++) {
      var root = cards[i].closest('ul, ol') || cards[i].parentElement;
      if (root && roots.indexOf(root) === -1) roots.push(root);
    }
    return roots;
  }

  function observeGrids() {
    if (typeof MutationObserver === 'undefined') return;

    for (var i = 0; i < observers.length; i++) {
      observers[i].disconnect();
    }
    observers = [];

    var roots = gridRoots();
    for (var j = 0; j < roots.length; j++) {
      var observer = new MutationObserver(handleMutations);
      observer.observe(roots[j], { childList: true, subtree: true });
      observers.push(observer);
    }
  }

  /* ----------------------------------------------------------------- init */

  function init() {
    updateAll(document);
    observeGrids();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
